/**
 * useCreateListView — tworzenie widoku listy kartoteki: „Nowy widok" (standardowe kolumny)
 * i „Duplikuj" (kopia konfiguracji pod unikalną nazwą „… (kopia)"). Po zapisie kreator
 * przełącza się na nowy widok (`onSelect`). Wspólne dla listy widoków w nagłówku Kreatora Listy
 * (`ListViewSwitcher`) i paska „widok tylko do odczytu — zrób kopię" na stronie kreatora.
 *
 * Trasa: POST na kolekcję widoków kartoteki — `/system-modules/:slug/layouts` (kartoteka
 * systemowa) albo `/modules/:id/layouts` (kartoteka modułu); adres daje `collectionEndpointFor`.
 */

import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

import type { TableLayout, TableLayoutConfig } from '@/components/configurable-table/types';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';

const P = 'tenant.listBuilder.views';
export const LIST_VIEW_NAME_MAX = 100;

/** Kody odmowy serwera dla widoku tylko do odczytu (centralny ITE / z menu modułu). */
const READ_ONLY_ERRORS = new Set(['CENTRAL_LAYOUT_READONLY', 'MENU_LAYOUT_READONLY']);

/** „Nowy widok" startuje bez zapisanych kolumn = standardowe kolumny listy. */
const EMPTY_LIST_CONFIG: TableLayoutConfig = {
  columns: [],
  defaultSort: null,
  defaultFilters: [],
  formattingRules: [],
  quickFilters: [],
  defaultViewMode: 'list',
};

/** Widok centralny (biblioteka widoków ITE) albo z menu modułu — treść tylko do odczytu. */
export function isReadOnlyListView(layout: TableLayout): boolean {
  return Boolean(layout.sourceViewSlug) || Boolean(layout.menuSourceModuleId);
}

/** Komunikat błędu zapisu widoku listy (osobny dla widoku tylko do odczytu). */
export function listViewErrorKey(err: unknown): string {
  const code = isAxiosError(err) ? (err.response?.data as { error?: string })?.error : undefined;
  return code && READ_ONLY_ERRORS.has(code) ? `${P}.errors.readOnly` : `${P}.errors.generic`;
}

export function useCreateListView({
  collection,
  availableLayouts,
  onSelect,
  onCreated,
}: {
  /** Kolekcja widoków kartoteki (`collectionEndpointFor(tableId)`). */
  collection: string;
  availableLayouts: TableLayout[];
  onSelect: (layoutId: string) => void;
  /** Po udanym zapisie (np. zamknięcie okna nazwy). */
  onCreated?: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();

  // Nazwy widoków listy nie powtarzają się w kartotece (bez względu na wielkość liter) — jak w
  // imporcie. Sprawdzenie w przeglądarce, na liście widoków tej kartoteki.
  function isNameTaken(name: string, exceptId?: string): boolean {
    const lower = name.toLowerCase();
    return availableLayouts.some((l) => l.id !== exceptId && l.name.toLowerCase() === lower);
  }

  const mutation = useMutation({
    mutationFn: async (input: { name: string; source?: TableLayout }) => {
      // Kreator jeszcze nie zna kartoteki (np. kartoteka modułu w trakcie wczytywania) —
      // bez adresu nie wysyłamy nic, zamiast strzelić POST w pusty adres.
      if (!collection) throw new Error('LIST_VIEW_COLLECTION_UNKNOWN');
      const res = await api.post<{ layout: { id: string; name: string } }>(collection, {
        layoutType: 'list',
        name: input.name,
        isDefault: false,
        layoutConfig: {},
        listConfig: input.source?.layoutConfig ?? EMPTY_LIST_CONFIG,
        roleIds: input.source?.roleIds ?? [],
        // Kopia zostaje przy kategorii źródła — inaczej widok kategorii stawałby się uniwersalny.
        ...(input.source?.categoryId ? { categoryId: input.source.categoryId } : {}),
      });
      return { ...res.data.layout, duplicated: Boolean(input.source) };
    },
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: ['table-layouts'] });
      onSelect(created.id);
      onCreated?.();
      toast.show({
        variant: 'success',
        title: t(created.duplicated ? `${P}.toast.duplicated` : `${P}.toast.created`, {
          name: created.name,
        }),
      });
    },
    onError: (err) => toast.show({ variant: 'error', title: t(listViewErrorKey(err)) }),
  });

  function duplicate(layout: TableLayout) {
    const base = t(`${P}.duplicateName`, { name: layout.name }).slice(0, LIST_VIEW_NAME_MAX);
    let name = base;
    for (let n = 2; isNameTaken(name); n += 1) {
      const suffix = ` ${n}`;
      name = `${base.slice(0, LIST_VIEW_NAME_MAX - suffix.length)}${suffix}`;
    }
    mutation.mutate({ name, source: layout });
  }

  return {
    create: (name: string) => mutation.mutate({ name }),
    duplicate,
    isNameTaken,
    isPending: mutation.isPending,
  };
}
